"use client"

import React from "react"
import { Combobox, useFilter, useListCollection } from "@chakra-ui/react"

const FALLBACK_LUGARES = [
  { label: "Bosques del Valle", value: "bosques_del_valle" },
  { label: "Fundidora", value: "fundidora" },
  { label: "Rufino Tamayo", value: "rufino_tamayo" },
]

export function LugarCombobox({
  items = [],
  label = "Lugar",
  placeholder = "Escribe para buscar...",
  value,          // string (id)
  onChange,       // (nextValue: string) => void
}) {
  const { contains } = useFilter({ sensitivity: "base" })

  const safeItems = Array.isArray(items) && items.length ? items : FALLBACK_LUGARES

  const { collection, filter } = useListCollection({
    initialItems: safeItems,
    itemToString: (item) => item?.label ?? "",
    itemToValue: (item) => item?.value ?? "",
    filter: (itemText, query) => contains(itemText ?? "", query ?? ""),
  })

  const handleEnterSelectFirst = React.useCallback(
    (e) => {
      if (e.key !== "Enter") return
      const first = collection.items[0]
      if (!first) return
      e.preventDefault()
      e.stopPropagation()
      onChange?.(first.value)
    },
    [collection.items, onChange]
  )

  return (
    <Combobox.Root
      collection={collection}
      openOnClick
      openOnChange={() => true}
      positioning={{ strategy: "fixed", hideWhenDetached: true }}
      value={value ? [value] : []}
      onValueChange={(e) => onChange?.(e.value?.[0] ?? "")}
      onInputValueChange={(e) => filter(e.inputValue ?? "")}
    >
      <Combobox.Label fontSize="sm" fontWeight="600" color="dashboard.fg.default">{label}</Combobox.Label>

      <Combobox.Control
        h="11"
        px="4"
        rounded="pill"
        bg="dashboard.bg.input"
        borderWidth="2px"
        borderColor="dashboard.border.default"
        transition="all 160ms ease"
        _hover={{ borderColor: "dashboard.accent.border", bg: "dashboard.bg.surfaceMuted" }}
        _focusWithin={{ borderColor: "dashboard.accent.border", boxShadow: "0 0 0 3px rgba(229,57,53,0.15)", bg: "dashboard.bg.input" }}
      >
        <Combobox.Input
          unstyled
          placeholder={placeholder}
          fontWeight="600"
          color="dashboard.fg.default"
          h="full"
          minW="0"
          flex="1"
          bg="transparent"
          borderWidth="0"
          outline="none"
          boxShadow="none"
          _placeholder={{ color: "dashboard.fg.subtle" }}
          _focus={{ outline: "none", boxShadow: "none" }}
          _focusVisible={{ outline: "none", boxShadow: "none" }}
          onKeyDownCapture={handleEnterSelectFirst}
        />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          <Combobox.Trigger />
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Combobox.Positioner zIndex="modal">
        <Combobox.Content
          bg="dashboard.bg.popover"
          color="dashboard.fg.default"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          rounded="xl"
          boxShadow="0 20px 38px rgba(183,28,28,0.10)"
          maxH="240px"
          overflowY="auto"
          p="1.5"
        >
          <Combobox.Empty px="2" py="2">
            No hay resultados
          </Combobox.Empty>

          {collection.items.map((item) => (
            <Combobox.Item key={item.value} item={item} px="3" py="2" rounded="lg" fontWeight="600" _highlighted={{ bg: "dashboard.accent.subtle", color: "dashboard.accent.fg" }}>
              <Combobox.ItemText>{item.label}</Combobox.ItemText>
              <Combobox.ItemIndicator />
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  )
}
